import { useState, useEffect, lazy, Suspense } from "react";
import { Analytics } from "@vercel/analytics/react";
import { signOut, restoreSession } from "./lib/auth";
import { C } from "./lib/colors";
import { FontLink } from "./components/Fonts";
import { GlobalStyles } from "./components/GlobalStyles";
import { ChatWidget } from "./components/ChatWidget";
import { Toasts } from "./components/Toasts";
import { ConfirmDialogHost } from "./components/ConfirmDialog";
import { SpinningO } from "./components/SpinningO";
import { LandingPage } from "./pages/LandingPage";
import { HowItWorksPage } from "./pages/HowItWorksPage";
import { PricingPage } from "./pages/PricingPage";
import { ContactPage } from "./pages/ContactPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { WelcomePage } from "./pages/WelcomePage";

// Code-split the three heaviest sub-trees so anonymous landing-page visitors
// don't download AgencyOS / portal / onboarding code they'll never run.
// React.lazy expects a default export; the .then() shim maps the named export
// without touching those files.
const OnboardingPage   = lazy(() => import("./pages/OnboardingPage")   .then(m => ({ default: m.OnboardingPage })));
const AgencyOSPage     = lazy(() => import("./pages/AgencyOS")         .then(m => ({ default: m.AgencyOSPage })));
const ClientPortalPage = lazy(() => import("./pages/ClientPortal")     .then(m => ({ default: m.ClientPortalPage })));

// Loading fallback shown while a lazy-loaded page chunk downloads
// (OnboardingPage, AgencyOS, ClientPortal). The wobbling steering-wheel O
// is MOAP's brand mark — using it here ties the loading state to the
// brand rather than a generic spinner.
const LoadingScreen = () => (
  <div style={{minHeight:"100vh",background:"#000000",display:"flex",
    alignItems:"center",justifyContent:"center"}}>
    <div style={{display:"flex",alignItems:"center",gap:14,
      fontFamily:"'DM Mono',monospace",fontSize:11,letterSpacing:2,
      color:C.green,textTransform:"uppercase"}}>
      <SpinningO size={36} mode="wobble"/>
      Loading
    </div>
  </div>
);

// New clients clicking the invite email land at /welcome with a Supabase
// recovery token in the URL hash. Detect that on first paint so we can show
// the right page immediately instead of flashing the landing page.
const PATH_TO_PAGE = {
  "/":                 "landing",
  "/how":              "how",
  "/pricing":          "pricing",
  "/onboarding":       "onboarding",
  "/contact":          "contact",
  "/privacy":          "privacy",
  "/terms":            "terms",
  "/login":            "login",
  "/welcome":          "welcome",
  "/reset-password":   "reset-password",
  "/forgot-password":  "forgot-password",
};

const initialPage = () => {
  if (typeof window === "undefined") return "landing";
  return PATH_TO_PAGE[window.location.pathname] || "landing";
};

export default function App() {
  const [page,       setPage]       = useState(initialPage);
  const [authRole,   setAuthRole]   = useState(null);   // "owner" | "client" | null
  const [clientUser, setClientUser] = useState(null);
  // Gates the first paint until we've checked Supabase for an existing session.
  // Otherwise a refresh briefly renders as "logged out" before redirecting back,
  // and on protected pages the user gets bounced to landing.
  const [sessionLoaded, setSessionLoaded] = useState(false);

  const navigate = (p) => { setPage(p); window.scrollTo({top:0,behavior:"smooth"}); };

  const ownerLogin  = ()       => { setAuthRole("owner");  setPage("agents"); };
  const clientLogin = (client) => { setAuthRole("client"); setClientUser(client); setPage("portal"); };
  const logout      = async () => {
    await signOut();
    setAuthRole(null); setClientUser(null); setPage("landing");
  };

  // ── Session restore on mount ────────────────────────────────────────────────
  // The Supabase JS client persists JWTs in localStorage by default, but the
  // app's React state starts as { authRole: null } on every load. Without this
  // effect, refreshing the page makes the user look "logged out" — they end up
  // at the landing page and have to sign in again, even though their session
  // is still valid. We pull the session at mount, resolve the role from the
  // existing helper, and re-route the user back to their workspace.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const restored = await restoreSession();
      if (cancelled) return;
      if (restored?.role === "owner") {
        setAuthRole("owner");
        // Only auto-route to /agents when they landed on a "neutral" page
        // (root, landing). If they refreshed on /login or /pricing, respect that.
        if (page === "landing") setPage("agents");
      } else if (restored?.role === "client") {
        setAuthRole("client");
        setClientUser(restored.client);
        if (page === "landing") setPage("portal");
      }
      setSessionLoaded(true);
    })();
    return () => { cancelled = true; };
    // Intentionally empty deps — only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hold the first paint until session restore finishes. Brief (~100-300ms);
  // matches the lazy-chunk LoadingScreen so the transition feels intentional.
  if (!sessionLoaded) {
    return (<>
      <FontLink/>
      <GlobalStyles/>
      <LoadingScreen/>
    </>);
  }

  // Guard: protected pages bounce to login if not authed.
  const guardedNavigate = (p) => {
    if ((p==="agents"||p==="portal") && !authRole) { navigate("login"); return; }
    navigate(p);
  };

  return (
    <>
      <FontLink/>
      <GlobalStyles/>
      {page==="landing"    && <LandingPage      onNavigate={guardedNavigate} authRole={authRole}/>}
      {page==="how"        && <HowItWorksPage   onNavigate={guardedNavigate} authRole={authRole}/>}
      {page==="pricing"    && <PricingPage      onNavigate={guardedNavigate} authRole={authRole}/>}
      {page==="contact"    && <ContactPage      onNavigate={guardedNavigate} authRole={authRole}/>}
      {page==="privacy"    && <PrivacyPage      onNavigate={guardedNavigate} authRole={authRole}/>}
      {page==="terms"      && <TermsPage        onNavigate={guardedNavigate} authRole={authRole}/>}
      {page==="login"            && <LoginPage          onNavigate={guardedNavigate} onOwnerLogin={ownerLogin} onClientLogin={clientLogin}/>}
      {page==="forgot-password"  && <ForgotPasswordPage  onNavigate={guardedNavigate}/>}
      {page==="reset-password"   && <ResetPasswordPage   onNavigate={guardedNavigate} onOwnerLogin={ownerLogin} onClientLogin={clientLogin}/>}
      {page==="welcome"          && <WelcomePage         onNavigate={guardedNavigate} onClientLogin={clientLogin}/>}
      {page==="onboarding" && (
        <Suspense fallback={<LoadingScreen/>}>
          <OnboardingPage onNavigate={guardedNavigate} authRole={authRole}/>
        </Suspense>
      )}
      {page==="agents" && authRole==="owner" && (
        <Suspense fallback={<LoadingScreen/>}>
          <AgencyOSPage onNavigate={guardedNavigate} onLogout={logout}/>
        </Suspense>
      )}
      {page==="portal" && authRole==="client" && (
        <Suspense fallback={<LoadingScreen/>}>
          <ClientPortalPage client={clientUser} onLogout={logout}/>
        </Suspense>
      )}
      {page!=="agents" && <ChatWidget/>}
      <Toasts/>
      <ConfirmDialogHost/>
      {/* Analytics only mounts in non-dev builds. Avoids a noisy "Invalid hook
          call" warning in `npm run dev` while keeping prod + Vercel previews
          tracked normally (both are built with import.meta.env.PROD === true). */}
      {!import.meta.env.DEV && <Analytics/>}
    </>
  );
}
